import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react'
import Calendar, { CalendarProps } from 'react-calendar'
import 'react-calendar/dist/Calendar.css'
import { parse, format as formatDate, isValid, parseISO } from 'date-fns'
import { DATE_FORMAT } from '../../../constants'
import { defaultTheme } from '../../../theme'
import { useTranslation } from 'react-i18next'
import * as Styled from './styles'

export type RangeDatePickerProps = {
  start: string
  end: string
  onChange: ([startDate, endDate]: [string, string]) => void
  onClear?: () => void
  format?: string
  calendarProps?: CalendarProps
  placeholder?: string
  optional?: boolean
  invalid?: boolean
  errorText?: string
  isClearable?: boolean
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>

export const RangeDatePicker: React.FC<RangeDatePickerProps> = ({
  start,
  end,
  onChange,
  onClear,
  format = DATE_FORMAT,
  calendarProps,
  placeholder = '',
  optional,
  invalid,
  errorText = '',
  isClearable,
  onFocus,
  onBlur,
  ...divProps
}) => {
  const { t } = useTranslation('ui')
  const [showCalendar, setShowCalendar] = useState(false)
  const timerId = useRef<number>(0)

  const dateRange: [Date | null, Date | null] = useMemo(() => {
    const startDate = parseISO(start)
    const endDate = parseISO(end)
    return [isValid(startDate) ? startDate : new Date(), isValid(endDate) ? endDate : new Date()]
  }, [start, end])

  const [startInputValue, setStartInputValue] = useState(start ? formatDate(parseISO(start), format) : '')
  const [endInputValue, setEndInputValue] = useState(end ? formatDate(parseISO(end), format) : '')

  useEffect(() => {
    setStartInputValue(start ? formatDate(parseISO(start), format) : '')
  }, [format, start])

  useEffect(() => {
    setEndInputValue(end ? formatDate(parseISO(end), format) : '')
  }, [end, format])

  const handleStartInputChange = useCallback(
    e => {
      const value = e.target.value.replace(/[^.\/0-9]+/, '')

      if (!value && !endInputValue) {
        onChange(['', ''])
      }
      if (value.length > 8) {
        return
      }
      if (value.length === 8) {
        const start = parse(value, format, new Date())
        const end = parse(endInputValue, format, new Date())
        if (isValid(start) && isValid(end)) {
          onChange([start.toISOString(), end.toISOString()])
        }
      }
      setStartInputValue(value)
    },
    [endInputValue, format, onChange],
  )

  const handleEndInputChange = useCallback(
    e => {
      const value = e.target.value.replace(/[^.\/0-9]+/, '')

      if (!value && !startInputValue) {
        onChange(['', ''])
      }
      if (value.length > 8) {
        return
      }
      if (value.length === 8) {
        const start = parse(startInputValue, format, new Date())
        const end = parse(value, format, new Date())
        if (isValid(start) && isValid(end)) {
          onChange([start.toISOString(), end.toISOString()])
        }
      }
      setEndInputValue(value)
    },
    [format, onChange, startInputValue],
  )

  const handleClear = () => {
    setStartInputValue('')
    setEndInputValue('')
    onChange(['', ''])

    onClear && onClear()
  }

  const isDateRangeSet = useMemo(() => (!start && !end) || (isValid(parseISO(start)) && isValid(parseISO(end))), [
    end,
    start,
  ])

  const isInputValid = !invalid && isDateRangeSet

  const isClearIconVisible = isClearable || onClear

  const ref = useRef<HTMLDivElement>(null)

  // like onBlur function but for Safari
  const handleClickOut = event => {
    if (!ref.current?.contains(event.target)) {
      timerId.current = window.setTimeout(() => setShowCalendar(false), 0)
    }
  }

  return (
    <Styled.Wrapper
      {...divProps}
      ref={ref}
      onFocus={(...args) => {
        clearTimeout(timerId.current)
        setShowCalendar(true)
        window.addEventListener('click', handleClickOut)
        onFocus?.(...args)
      }}
      onBlur={(...args) => {
        window.removeEventListener('click', handleClickOut)
        onBlur?.(...args)
      }}
    >
      <Styled.InputWrapper isFocused={showCalendar} isValid={isInputValid}>
        <Styled.Input
          value={startInputValue}
          placeholder={placeholder || format?.toUpperCase()}
          onChange={handleStartInputChange}
        />
        <Styled.Dash />
        <Styled.Input
          value={endInputValue}
          placeholder={placeholder || format?.toUpperCase()}
          onChange={handleEndInputChange}
        />
        {isClearIconVisible && <Styled.ClearIcon onClick={handleClear} />}
        {optional && <Styled.Optional>{t('common.optional')}</Styled.Optional>}
      </Styled.InputWrapper>
      {invalid && <Styled.ErrorText>{errorText}</Styled.ErrorText>}
      <Styled.CalendarContainer style={{ display: showCalendar ? 'block' : 'none', zIndex: defaultTheme.zIndex.popup }}>
        <Calendar
          value={dateRange as [Date, Date]}
          onChange={(value: unknown) => {
            const startDate = (value as Date[])[0]
            const endDate = (value as Date[])[1]
            onChange([startDate.toISOString(), endDate.toISOString()])
            setStartInputValue(formatDate(startDate, format))
            setEndInputValue(formatDate(endDate, format))
            setShowCalendar(false)
          }}
          selectRange
          {...calendarProps}
        />
      </Styled.CalendarContainer>
    </Styled.Wrapper>
  )
}
